import React, { useEffect } from 'react';
import { ENTITIES, FILTER_ENTITY, TAB, POOL_REFINEMENT, SECTION_TYPE, definitionAsRow, SELECTION, requiresCircleIndex } from '../lib/row-definition-search';
import { allowedOptions } from '../lib/row-definition-helpers';
import Select from 'react-select';
import { useRowForm } from '../RowFormContext';
import './RowDefinitionBuilder.scss';

const allowedOptionsAsSelectOptions = (definition, options) => allowedOptions(definition, options).map(opt => ({ label: opt.label, value: opt.key }));

export const RowDefinitionBuilderSearch = () => {
  const { setFilter, setCardSort, setEntity, definition, setDefinition } = useRowForm();

  const allowedTabOptions = allowedOptionsAsSelectOptions(definition, TAB);
  const allowedPoolRefinementOptions = allowedOptionsAsSelectOptions(definition, POOL_REFINEMENT);
  const allowedSectionTypeOptions = allowedOptionsAsSelectOptions(definition, SECTION_TYPE);
  const allowedSelectionOptions = allowedOptionsAsSelectOptions(definition, SELECTION);

  /*
   * Auto-select fields when only one valid option exists or when a default is available
   */
  useEffect(() => {
    const allowedTabOptionsRaw = allowedOptions(definition, TAB);
    const allowedPoolRefinementOptionsRaw = allowedOptions(definition, POOL_REFINEMENT);
    const allowedSectionTypeOptionsRaw = allowedOptions(definition, SECTION_TYPE);
    const allowedSelectionOptionsRaw = allowedOptions(definition, SELECTION);

    const autoSelects = {
      tab: allowedTabOptionsRaw,
      poolRefinement: allowedPoolRefinementOptionsRaw,
      sectionType: allowedSectionTypeOptionsRaw,
      selection: allowedSelectionOptionsRaw
    };

    const updates = {};

    Object.entries(autoSelects).forEach(([field, options]) => {
      // If there's only one option, always select it
      if (options.length === 1 && definition[field] !== options[0].key) {
        updates[field] = options[0].key;
      }
      // If the field is empty and there's a default option, select it
      else if (!definition[field] && options.length > 1) {
        const defaultOption = options.find(opt => opt.default === true);
        if (defaultOption) {
          updates[field] = defaultOption.key;
        }
      }
    });

    if (Object.keys(updates).length > 0) {
      setDefinition(prev => ({ ...prev, ...updates }));
    }
  }, [
    definition.entity,
    definition.filterEntity,
    definition.tab,
    definition.poolRefinement,
    definition.sectionType,
    definition.selection,
    setDefinition
  ]);

  useEffect(() => {
    const asRow = definitionAsRow(definition);

    setEntity(asRow.entity);
    setFilter(asRow.filter);
    setCardSort(asRow.cardSort);
  }, [definition, setFilter, setCardSort, setEntity]);

  return (
    <div className='row-definition-builder'>
      <label>
        <span className='label-text'>Entity</span>
        <Select
          className='select'
          options={Object.values(ENTITIES).map(entity => ({ label: entity, value: entity }))}
          placeholder='Select entity'
          value={definition.entity ? { label: definition.entity, value: definition.entity } : undefined}
          onChange={option => setDefinition(prev => ({ ...prev, entity: option?.value || null }))}
        />
      </label>

      <div className='entity-filter-options'>
        <label>
          <span className='label-text'>Entity Filter</span>
          <Select
            className='select'
            options={Object.values(FILTER_ENTITY).map(entity => ({ label: entity, value: entity }))}
            placeholder='Select entity filter'
            value={definition.filterEntity ? { label: definition.filterEntity, value: definition.filterEntity } : undefined}
            onChange={option => setDefinition(prev => ({ ...prev, filterEntity: option?.value || null }))}
          />
        </label>

        {allowedSelectionOptions.length > 1 && (
          <label>
            <span className='label-text'>Selection</span>
            <Select
              className='select'
              options={allowedSelectionOptions}
              placeholder='Select selection'
              value={allowedSelectionOptions.find(opt => opt.value === definition.selection) || undefined}
              onChange={option => setDefinition(prev => ({ ...prev, selection: option?.value || null }))}
            />
          </label>
        )}

        {definition.selection === SELECTION.FIXED.key && (
          <label className='entity-id-input'>
            <span className='label-text'>ID</span>
            <input
              type='text'
              className='id-input'
              placeholder={`Enter ${definition.filterEntity} ID`}
              value={definition.Entity_id || ''}
              onChange={e => {
                const value = e.target.value;
                setDefinition(prev => ({ ...prev, Entity_id: value }));
              }}
            />
          </label>
        )}
      </div>

      {allowedPoolRefinementOptions.length > 1 && (
        <label>
          <span className='label-text'>within</span>
          <Select
            className='select'
            options={allowedPoolRefinementOptions}
            placeholder='Select pool refinement'
            value={allowedPoolRefinementOptions.find(opt => opt.value === definition.poolRefinement) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, poolRefinement: option?.value || null }))}
          />
        </label>
      )}

      {requiresCircleIndex(definition) && (
        <label>
          <span className='label-text'>Circle index</span>
          <input
            type='number'
            className='circle-index-input'
            min='0'
            step='1'
            placeholder='0 = first circle'
            value={definition.circleIndex ?? ''}
            onChange={e => setDefinition(prev => ({ ...prev, circleIndex: e.target.value }))}
          />
        </label>
      )}

      {allowedTabOptions.length > 1 && (
        <label>
          <span className='label-text'>Tab</span>
          <Select
            className='select'
            options={allowedTabOptions}
            placeholder='Select tab'
            value={allowedTabOptions.find(opt => opt.value === definition.tab) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, tab: option?.value || null }))}
          />
        </label>
      )}

      {allowedSectionTypeOptions.length > 1 && (
        <label>
          <span className='label-text'>Section type</span>
          <Select
            className='select'
            options={allowedSectionTypeOptions}
            placeholder='Select section type'
            value={allowedSectionTypeOptions.find(opt => opt.value === definition.sectionType) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, sectionType: option?.value || null }))}
          />
        </label>
      )}
    </div>
  );
};
